import { useState, useEffect, useCallback } from 'react'
import { AlertOctagon, LogIn, LogOut, MapPin, Check, CheckCheck, BellOff, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { equipmentOf } from '../equipment'
import { useConfirm } from './ConfirmDialog'
import { LoadingState, ErrorState, EmptyState } from './ViewState'
import { fmtRelative } from '../i18n/format'

const TYPES = {
  violation: {
    Icon: AlertOctagon,
    key: 'violation',
    color: '#f04438',
    text: 'text-status-down',
    soft: 'bg-status-down/10',
    border: 'border-status-down',
  },
  entry: {
    Icon: LogIn,
    key: 'entry',
    color: '#f79009',
    text: 'text-status-stop',
    soft: 'bg-status-stop/10',
    border: 'border-status-stop',
  },
  exit: {
    Icon: LogOut,
    key: 'exit',
    color: '#175cd3',
    text: 'text-brand',
    soft: 'bg-brand/10',
    border: 'border-brand',
  },
}

const typeOf = (t) => TYPES[t] || TYPES.violation

function fmtCoord(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(5) : null
}

function relTime(created_at) {
  if (!created_at) return ''
  const d = new Date(created_at)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return fmtRelative(d)
  } catch {
    return ''
  }
}

function AlarmCard({ alarm, device, categories, mines, onAck }) {
  const { t } = useTranslation()
  const at = typeOf(alarm.alarm_type)
  const eq = equipmentOf(device, categories, mines)
  const ack = !!alarm.acknowledged
  const fence = alarm.geofence_name || t('ops:alarms.fence_fallback', { id: alarm.geofence_id ?? '' }).trim()
  const lat = fmtCoord(alarm.lat)
  const lon = fmtCoord(alarm.lon)

  return (
    <div
      className={`flex items-start gap-4 bg-white dark:bg-slate-900 rounded-xl border shadow-sm transition-all p-4 ${
        ack ? 'border-slate-200 dark:border-slate-700 opacity-60' : `${at.border} shadow-md`
      }`}
    >
      <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${at.soft} ${at.text}`}>
        <at.Icon size={20} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-800 dark:text-slate-100">
          <span className="font-semibold">{eq.label}</span> {t(`ops:alarms.phrase.${at.key}`)}
          {alarm.geofence_name ? <> <span className="font-semibold">{alarm.geofence_name}</span></> : null}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[12px] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1.5">
            <span
              className="equip-icon block w-4 h-4 text-slate-400 dark:text-slate-500"
              style={{
                WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`,
                maskImage: `url(/equipments/${eq.svg}.svg)`,
              }}
            />
            {alarm.device_name || t('common:entities.equipment')}
          </span>
          <span>{relTime(alarm.created_at)}</span>
          {lat && lon ? (
            <span className="flex items-center gap-1">
              <MapPin size={12} /> {lat}, {lon}
            </span>
          ) : null}
        </div>
      </div>

      <div className="shrink-0 flex items-center">
        {ack ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
            <Check size={12} /> {t('ops:alarms.acknowledged')}
          </span>
        ) : (
          <button
            onClick={() => onAck(alarm.id)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Check size={14} /> {t('ops:alarms.acknowledge')}
          </button>
        )}
      </div>
    </div>
  )
}

export default function AlarmsView() {
  const { t } = useTranslation()
  const ws = useWS() || {}
  const { alarms, connected, ackAlarm, ackAllAlarms, clearAlarms, devices = [], categories = [], mines = [] } = ws
  const { mineScope, matchesScope, mineQS, scopeLabel } = useScope()

  const [local, setLocal] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [onlyUnack, setOnlyUnack] = useState(false)
  const [confirmDialog, ask] = useConfirm()

  // O WebSocket manda o snapshot de alarmes na conexão, então o REST só é o caminho de
  // recuperação: sem WS de pé, buscar por HTTP em vez de mostrar a tela vazia (que lia
  // como "nenhum alarme" justamente durante um restart do backend).
  const loadRest = useCallback(() => {
    setLoading(true)
    setError(null)
    fetch('/api/geofence-alarms?limit=200')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then((data) => setLocal(Array.isArray(data) ? data : data?.alarms || []))
      .catch((err) => { setError(err.message); setLocal(null) })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { if (!connected) loadRest() }, [connected, loadRest])

  const live = connected && Array.isArray(alarms)
  const allAlarms = (live ? alarms : local) || []
  const deviceByName = (name) => devices.find((d) => d.name === name) || { name }
  // Escopo global de mina: o alarme segue o device que o gerou.
  const list = mineScope === 'all' ? allAlarms : allAlarms.filter((a) => matchesScope(deviceByName(a.device_name)))
  const unackCount = list.filter((a) => !a.acknowledged).length
  const shown = onlyUnack ? list.filter((a) => !a.acknowledged) : list

  const handleAck = (id) => ackAlarm?.(id)
  const handleAckAll = () => ackAllAlarms?.()
  // O DELETE leva o escopo de mina da tela: sem escopo apaga o histórico de todas as
  // minas, inclusive alarmes não reconhecidos de quem não está aqui. O diálogo diz qual
  // dos dois vai acontecer e conta os alarmes do escopo.
  const handleClear = () => {
    ask({
      title: t('ops:alarms.clear_title'),
      message: mineQS
        ? t('ops:alarms.clear_message_scoped', { n: list.length, mine: scopeLabel })
        : t('ops:alarms.clear_message', { n: allAlarms.length }),
      confirmWord: t('ops:alarms.clear_word'),
      confirmLabel: t('common:actions.clear'),
      danger: true,
      onConfirm: async () => { await clearAlarms?.(mineQS); if (!connected) loadRest() },
    })
  }

  return (
    <div className="h-full flex flex-col">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-slate-400">{t('ops:alarms.unacknowledged')}</span>
          <span
            className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-semibold ${
              unackCount > 0 ? 'bg-status-down/10 text-status-down' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500'
            }`}
          >
            {unackCount}
          </span>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyUnack}
            onChange={(e) => setOnlyUnack(e.target.checked)}
            className="rounded border-slate-300 dark:border-slate-600 text-brand focus:ring-brand"
          />
          {t('ops:alarms.only_unacknowledged')}
        </label>

        <div className="flex-1" />

        <button
          onClick={handleAckAll}
          disabled={unackCount === 0}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <CheckCheck size={16} /> {t('ops:alarms.acknowledge_all')}
        </button>

        <button
          onClick={handleClear}
          disabled={allAlarms.length === 0}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-status-down/40 text-status-down hover:bg-status-down/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 size={16} /> {t('common:actions.clear')}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        {loading && shown.length === 0 ? (
          <LoadingState label={t('ops:alarms.loading')} />
        ) : error && shown.length === 0 ? (
          <ErrorState message={error} onRetry={loadRest} />
        ) : shown.length === 0 ? (
          <EmptyState icon={BellOff} message={onlyUnack ? t('ops:alarms.none_unack') : t('ops:alarms.none')} />
        ) : (
          <div className="flex flex-col gap-3 max-w-4xl mx-auto">
            {shown.map((a) => (
              <AlarmCard
                key={a.id ?? `${a.device_name}-${a.created_at}`}
                alarm={a}
                device={deviceByName(a.device_name)}
                categories={categories}
                mines={mines}
                onAck={handleAck}
              />
            ))}
          </div>
        )}
      </div>
      {confirmDialog}
    </div>
  )
}
